import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';

import { AuthShell } from '@/components/auth/AuthShell';
import { Button } from '@/components/ui/button';
import { authService } from '@/services/authService';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

type Status = 'verifying' | 'success' | 'expired' | 'invalid' | 'error';

const REDIRECT_DELAY_MS = 2000;

export function VerifyEmailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, refreshUser } = useAuth();
  const [status, setStatus] = useState<Status>('verifying');

  // StrictMode-safe: guarantee a single network call per page mount.
  const dispatchedRef = useRef(false);
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const token = searchParams.get('token') || '';

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      return;
    }
    // StrictMode-safe one-shot: dispatchedRef survives the dev-mode
    // unmount/remount cycle, so we issue exactly one network call. No
    // "cancelled" flag — a stale closure cancelled set by the StrictMode
    // cleanup would block the only state update we ever make and freeze
    // the page on "verifying".
    if (dispatchedRef.current) return;
    dispatchedRef.current = true;

    authService
      .confirmVerification(token)
      .then(async () => {
        setStatus('success');

        if (isAuthenticated) {
          try {
            await refreshUser();
          } catch {
            // ignore — banner will fix itself on next /me hydrate
          }
        }

        redirectTimerRef.current = setTimeout(() => {
          if (isAuthenticated) {
            navigate('/', { replace: true });
          } else {
            navigate('/login', { replace: true, state: { flash: 'email_verified' } });
          }
        }, REDIRECT_DELAY_MS);
      })
      .catch((err) => {
        if (err instanceof ApiError) {
          if (err.status === 410) setStatus('expired');
          else if (err.status === 400) setStatus('invalid');
          else setStatus('error');
        } else {
          setStatus('error');
        }
      });
  }, [token, isAuthenticated, refreshUser, navigate]);

  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
    };
  }, []);

  if (status === 'verifying') {
    return (
      <AuthShell title={t('auth.verify_title')}>
        <div className="flex justify-center py-8">
          <Loader2
            className="h-8 w-8 animate-spin text-muted-foreground"
            aria-label={t('common.loading')}
          />
        </div>
      </AuthShell>
    );
  }

  if (status === 'success') {
    return (
      <AuthShell title={t('auth.verify_success')}>
        <div className="flex flex-col items-center py-8 space-y-3">
          <CheckCircle2 className="h-12 w-12 text-foreground" aria-hidden="true" />
          <p className="text-sm text-muted-foreground">{t('common.loading')}…</p>
        </div>
      </AuthShell>
    );
  }

  const errorKey =
    status === 'expired'
      ? 'auth.verify_expired'
      : status === 'invalid'
        ? 'auth.verify_invalid'
        : 'common.error_generic';

  return (
    <AuthShell title={t(errorKey)}>
      <div className="space-y-6">
        <div className="flex justify-center py-4">
          <AlertCircle className="h-12 w-12 text-destructive" aria-hidden="true" />
        </div>
        <Link to="/login" className="block">
          <Button variant="default" className="w-full h-11">
            {t('auth.log_in')}
          </Button>
        </Link>
      </div>
    </AuthShell>
  );
}
